import React from "react";
import styled from "styled-components";
import { Icon } from "@material-ui/core";
import { v4 as uuid } from "uuid";

import { _ } from "js/vendor";
import { isEmpty } from "lodash-es";
import { app } from "js/namespaces";
import log from "js/core/logger";
import { UIPane, RoundIconButton } from "js/react/components/UiComponents";
import { FilterDropDown } from "js/react/views/AddSlide/Panes/Components/FilterDropDown";
import { Breadcrumb } from "js/editor/PresentationLibrary/PresentationLibrary";
import {
    SearchBarContainer,
    SearchBarInnerContainer,
    UIPaneResultsContainer,
    InnerWhiteFrameContainer,
    SlideSearchInput,
} from "js/react/views/AddSlide/Panes/Components/SearchBox";
import { SortMenu } from "js/react/views/AddSlide/Panes/Components/SortMenu";

import SortAlphabeticalVariant from "mdi-material-ui/SortAlphabeticalVariant";
import Update from "mdi-material-ui/Update";
import CalendarRange from "mdi-material-ui/CalendarRange";

import ImageThumbnailGrid from "js/react/views/AddAssets/Components/ImageThumbnailGrid";
import AssetPreview from "js/react/views/AddAssets/Components/AssetPreview";
import { ds } from "js/core/models/dataService";
import Api from "js/core/api";
import { ASSET_RESOLUTION, AssetType, PERMISSION_RESOURCE_TYPE } from "common/constants";
import { UIController } from "js/editor/dialogs/UIController";

import { ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import AddAssetsContainer from "js/react/views/AddAssets/AddAssetsContainer";
import { ShowConfirmationDialog } from "js/react/components/Dialogs/BaseDialog";
import { sortTagsByPopularity, trackActivity } from "js/core/utilities/utilities";
import { NoMatchNotice, NoTeamAssetNotice } from "js/react/components/Notice";
import CreateTeamAssetDialog from "./Dialogs/CreateTeamAssetDialog";
import { TeamAssetSettingsDialog } from "./Dialogs/TeamAssetSettingsDialog";
import getUserProfile from "js/core/services/userProfiles";
import { Gap } from "js/react/components/Gap";
import FetchingClickShield from "js/react/components/FetchingClickShield";

const TEAM_ASSET = PERMISSION_RESOURCE_TYPE.TEAM_ASSET;

const StyledUIPaneResultsContainer = styled(UIPaneResultsContainer)`
  ${props => props.editable ? "" : "height: 50px"}
`;

const StyledSearchBarContainer = styled(SearchBarContainer)`
    background: #eee;
    ${props => props.editable ? "" : "height: 50px"}
`;

const StyledSearchBarInnerContainer = styled(SearchBarInnerContainer)`
    background: ${props => props.editable ? "white" : "#eee"};
`;

const mediaFilterOptions = [
    {
        id: "all",
        name: "All Media"
    },
    {
        id: "image",
        name: "Photos"
    },
    {
        id: "logo",
        name: "Logos"
    },
];

const sortOptions = [
    {
        value: "assetName",
        label: "Name",
        icon: <SortAlphabeticalVariant />
    },
    {
        value: "createdAt",
        label: "Created At",
        reverse: true,
        icon: <CalendarRange />
    },
    {
        value: "modifiedAt",
        label: "Modified At",
        reverse: true,
        icon: <Update />
    },
];

export const genPreviewProps = async asset => {
    try {
        const previewProps = { ...asset.attributes };

        if (asset.type === AssetType.VIDEO || asset.type === AssetType.STOCK_VIDEO) {
            if (asset.type === AssetType.VIDEO) {
                previewProps.url = await asset.getURL();
                const previewAssetId = asset.get("previewAssetId");
                if (previewAssetId) {
                    const previewAsset = await ds.assets.getAssetById(previewAssetId, AssetType.IMAGE);
                    previewProps.previewUrl = await previewAsset.getURL();
                }
            }
        } else {
            const url = await asset.getURL(ASSET_RESOLUTION.SMALL);
            previewProps.url = url;
        }

        // hash for team assets, if any
        previewProps.hash = asset.get("hash");

        // Ensure we have width and height values
        previewProps.width = previewProps.width || previewProps.w;
        previewProps.height = previewProps.height || previewProps.h;

        previewProps.createdBy = await getUserProfile(previewProps.createdById);
        previewProps.modifiedBy = await getUserProfile(previewProps.modifiedById);

        return { asset, previewProps };
    } catch (error) {
        log.error(error);
        return { asset, error };
    }
};

export class TeamAssetsPane extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            loadedAssets: {},
            isLoading: true,
            muted: true,
            selectedMediaFilter: "all",
            selectedTagFilter: "  all  ", // Spaces needed to prevent conflict with other tags
            sortBy: "createdAt",
            sortDir: 1,
            searchTerm: props.prefilledSearchQuery?.trim().toLowerCase() || "",
        };
    }

    componentDidMount() {
        this.reloadAssets()
            .then(() => {
                const workspace = app.user.workspaces[app.appState.workspaceId];
                const team = workspace.getDefaultTeam();
                team?.on("change:sharedResources", this.onChangeSharedResources);
            })
            .catch(log.error);
    }

    onChangeSharedResources = () => {
        this.setState({ loadedAssets: {} }, () => {
            this.reloadAssets();
        });
    }

    componentWillUnmount() {
        const workspace = app.user.workspaces[app.appState.workspaceId];
        const team = workspace.getDefaultTeam();
        team?.off("change:sharedResources", this.onChangeSharedResources);
    }

    handleSearch = async searchTerm => {
        this.setState({
            searchTerm: searchTerm?.trim().toLowerCase(),
        });
    };

    async getAssetsToLoad(teamAssets) {
        const workspace = app.user.workspaces[app.appState.workspaceId];
        const team = workspace.getDefaultTeam();
        teamAssets = teamAssets ?? { ...(team?.get("sharedResources") ?? {})[TEAM_ASSET] };

        const assetIds = Object.keys(teamAssets);
        const type = AssetType.IMAGE; // HACK
        const assets = await Promise.all(assetIds.map(id => ds.assets.getAssetById(id, type)));

        // Load urls now
        const latestAssets = {
            ...this.state.loadedAssets,
            ...Object.fromEntries(await Promise.all(assets.map(async asset => ([asset.id, await genPreviewProps(asset)])))),
        };

        return latestAssets;
    }

    async reloadAssets() {
        this.setState({ isLoading: true });

        const loadedAssets = await this.getAssetsToLoad();
        await new Promise(resolve => this.setState({ loadedAssets }, resolve));

        // Hide spinner after the loaded assets are rendered, this
        // will also force the grid to reflow if needed
        this.setState({ isLoading: false });

        return loadedAssets;
    }

    async createOrUpdateTeamAsset(fromAssetId, attributesOverrides = {}, originalTeamAsset = null) {
        const updatedAttributes = {
            // Force org id and remove user id
            orgId: UIController.getOrganizationId(),
            userId: null,
            // This prop is used for pulling files
            actualId: fromAssetId,
            // Marking correct update data
            modifiedAt: Date.now(),
            modifiedById: app.user.id,
            ...attributesOverrides
        };

        let asset;
        if (originalTeamAsset) {
            // If original team asset is defined - then update it with the source
            // asset attributes and the new values

            const fromAsset = await ds.assets.getAssetById(fromAssetId);
            const newAttributes = {
                ...fromAsset.attributes,
                // Attributes of team asset we need to preserve
                assetName: originalTeamAsset.get("assetName"),
                tags: originalTeamAsset.get("tags") ?? [],
                hidden: originalTeamAsset.get("hidden") ?? false,
                content_type: originalTeamAsset.get("type"),
                createdById: originalTeamAsset.get("createdById"),
                ...updatedAttributes
            };
            delete newAttributes.id;

            asset = originalTeamAsset;
            asset.replace(newAttributes);
            await ds.assets.updatePromise;
        } else {
            // If creating team asset from a non-team asset that already exists - then clone it
            // NOTE: we won't be cloning files because we set the actualId field
            asset = await ds.assets.cloneAsset(fromAssetId, {
                ...updatedAttributes,
                // Weird field douplicating asset.userId, but we already rely on it...
                createdById: app.user.id,
                // Force id of the correct format
                id: `${UIController.getOrganizationId()}:${uuid()}`
            }, false);
        }

        // Ensure asset is in team's sharedResources
        const workspace = app.user.workspaces[app.appState.workspaceId];
        const team = workspace.getDefaultTeam();
        team.update({ sharedResources: { [TEAM_ASSET]: { [asset.id]: true } } });

        const loaddedAssets = await this.reloadAssets();

        return loaddedAssets[asset.id];
    }

    handleAddItem = async existingTags => {
        ShowDialog(AddAssetsContainer, {
            assetType: AssetType.IMAGE,
            hideTeamAssets: true,
            hideIcons: true,
            hideVideos: true,
            callback: async (model, isNew) => {
                const workspace = app.user.workspaces[app.appState.workspaceId];
                const team = workspace.getDefaultTeam();

                if (team) {
                    ShowDialog(CreateTeamAssetDialog, {
                        type: model.content_type,
                        name: model.assetName,
                        tags: model.tags,
                        existingTags,
                        callback: async ({
                            type,
                            name,
                            tags,
                        }) => {
                            const attributesOverrides = {
                                type,
                                content_type: type,
                                assetName: name,
                                tags
                            };

                            const { asset } = await this.createOrUpdateTeamAsset(model.content_value, attributesOverrides, null);

                            trackActivity("TeamAsset", "Added", null, null, {
                                id: asset.id,
                                orgId: asset.get("orgId"),
                                type: asset.get("type"),
                                isNew,
                            });
                        }
                    });
                }
            }
        });
    }

    handleEditSettings = async (asset, previewProps, existingTags) => {
        ShowDialog(TeamAssetSettingsDialog, {
            previewProps,
            existingTags,
            replaceAsset: this.handleReplaceItem,
            callback: async (assetId, delta) => {
                if (!isEmpty(delta)) {
                    const asset = await ds.assets.getAssetById(assetId);

                    // Update asset attributes
                    const assetValues = {
                        ...asset.attributes,
                        ...delta,
                        createdById: previewProps.createdById ?? app.user.id,
                        modifiedAt: Date.now(),
                        modifiedById: app.user.id,
                    };
                    asset.update(assetValues, {
                        replaceKeys: true,
                    });
                    await ds.assets.updatePromise;

                    trackActivity("TeamAsset", "Updated", null, null, {
                        id: assetValues.id,
                        orgId: assetValues.orgId,
                        type: assetValues.type,
                        values: assetValues,
                    });

                    await this.reloadAssets();
                }
            }
        });
    }

    handleReplaceItem = async (teamAsset, assetValueOverrides = {}) => {
        return new Promise(resolve => {
            const assetType = teamAsset.get("type");
            const showAssetsDialog = () => {
                ShowDialog(AddAssetsContainer, {
                    assetType,
                    hideTeamAssets: true,
                    hideIcons: true,
                    hideVideos: true,
                    hidePhotos: assetType !== AssetType.IMAGE,
                    hideLogos: assetType !== AssetType.LOGO,
                    onCancel: () => resolve(false),
                    callback: async (model, isNew) => {
                        const { asset, previewProps, error } = await this.createOrUpdateTeamAsset(model.content_value, assetValueOverrides, teamAsset);

                        trackActivity("TeamAsset", "Replaced", null, null, {
                            id: asset.id,
                            orgId: asset.get("orgId"),
                            type: asset.get("type"),
                            isNew
                        });

                        resolve({ asset, previewProps, error });
                    }
                });
            };

            ShowConfirmationDialog({
                title: "Continue changing this shared team asset?",
                message: "Changes will be applied to wherever this asset is used.",
                acceptCallback: showAssetsDialog,
                cancelCallback: () => resolve(false),
                okButtonLabel: "Continue"
            });
        });
    }

    handleDeleteItem = async asset => {
        if (await ShowConfirmationDialog({
            title: "Are you sure you want to delete this asset?",
            message: "This action can not be undone.",
            buttonOptions: { cancelButtonVariant: "outlined", acceptButtonColor: "red" },
            okButtonLabel: "Delete",
        })) {
            const workspace = app.user.workspaces[app.appState.workspaceId];
            const team = workspace.getDefaultTeam();

            if (team) {
                // Remove entry from team's sharedResources
                team.update({ sharedResources: { [TEAM_ASSET]: { [asset.id]: null } } });

                const assetValues = asset.attributes;

                trackActivity("TeamAsset", "Deleted", null, null, {
                    id: assetValues.id,
                    orgId: asset.get("orgId"),
                    type: asset.get("type")
                });

                // Update ui
                const loadedAssets = { ...this.state.loadedAssets };
                delete loadedAssets[asset.id];
                this.setState({ loadedAssets });
            }
        }
    }

    handleHideItem = async asset => {
        // flag as hidden
        asset.update({ hidden: true });
        this.forceUpdate();

        const assetValues = asset.attributes;

        trackActivity("TeamAsset", "Hidden", null, null, {
            id: assetValues.id,
            orgId: assetValues.orgId,
            type: assetValues.type,
        });
    }

    handleShowItem = async asset => {
        // flag as not hidden
        asset.update({ hidden: false });
        this.forceUpdate();

        const assetValues = asset.attributes;

        trackActivity("TeamAsset", "Shown", null, null, {
            id: assetValues.id,
            orgId: assetValues.orgId,
            type: assetValues.type,
        });
    }

    handleMediaFilterChange = event => {
        const selectedMediaFilter = event.target.value;
        this.setState({ selectedMediaFilter });
    }

    handleTagFilterChange = event => {
        const selectedTagFilter = event.target.value;
        this.setState({ selectedTagFilter });
    }

    handleSort = ({ field, reverse }) => {
        this.setState({
            sortBy: field,
            sortDir: reverse ? 1 : -1,
        });
    }

    getSortedFilteredAssets() {
        const {
            sortBy,
            sortDir,
            loadedAssets,
            searchTerm,
            selectedMediaFilter,
            selectedTagFilter,
        } = this.state;
        const {
            editable
        } = this.props;

        // filtering
        const assetEntries = Object.values(loadedAssets).filter(({ asset, error }) => {
            if (error) {
                return false;
            }

            if (
                !editable &&
                asset.get("hidden")
            ) {
                return false;
            }

            const tags = asset.get("tags") ?? [];

            const searchMatch = (
                !searchTerm?.length ||
                asset.get("assetName")?.toLowerCase().includes(searchTerm) ||
                tags.some(tag => tag.toLowerCase().includes(searchTerm))
            );
            const mediaMatch = (
                selectedMediaFilter === "all" ||
                asset.get("type") === selectedMediaFilter
            );
            const tagMatch = (
                selectedTagFilter === "  all  " ||
                tags.includes(selectedTagFilter)
            );

            return searchMatch && mediaMatch && tagMatch;
        });

        // sorting
        assetEntries.sort((a, b) => {
            const valueA = a.asset.get(sortBy);
            const valueB = b.asset.get(sortBy);
            return valueA < valueB ? sortDir : -sortDir;
        });

        return assetEntries;
    }

    getSortedTags() {
        const { editable } = this.props;
        const { loadedAssets } = this.state;

        const assetEntries = Object.values(loadedAssets).filter(({ asset, error }) => (
            !error && (
                editable ||
                !asset.get("hidden")
            )
        ));

        const sortedTags = sortTagsByPopularity(assetEntries.map(({ previewProps }) => previewProps.tags));

        return sortedTags;
    }

    render() {
        const {
            editable,
            addAssetCallback,
            handleConfirm,
        } = this.props;
        const {
            selectedMediaFilter,
            selectedTagFilter,
            sortBy,
            searchTerm,
            isLoading
        } = this.state;

        const existingTags = this.getSortedTags();
        const tagOptions = [
            {
                id: "  all  ", // Spaces needed to prevent conflict with other tags
                name: "All Tags",
            },
            ...existingTags.map(tag => {
                return {
                    id: tag,
                    name: tag,
                };
            }),
        ];

        const items = this.getSortedFilteredAssets();
        const isLibOrOwner = ds.teams.isUserLibrarianOrOwner();

        return (
            <UIPane>
                <StyledSearchBarContainer
                    className="search-container"
                    editable={editable}
                >
                    {editable && <>
                        <Breadcrumb>Team Assets</Breadcrumb>
                        <RoundIconButton
                            onClick={() => this.handleAddItem(existingTags)}
                        >
                            <Icon>add</Icon>
                        </RoundIconButton>
                        <Gap size={60} />
                    </>}
                    <StyledSearchBarInnerContainer editable={editable}>
                        <SlideSearchInput
                            query={searchTerm}
                            onSearch={this.handleSearch}
                            placeholder="Search team assets..."
                        />

                        <FilterDropDown
                            onChange={this.handleMediaFilterChange}
                            selectedFilter={selectedMediaFilter}
                            filterOptions={mediaFilterOptions}
                        />
                        <FilterDropDown
                            onChange={this.handleTagFilterChange}
                            selectedFilter={selectedTagFilter}
                            filterOptions={tagOptions}
                        />
                        <SortMenu
                            selectedOption={sortBy}
                            disabled={searchTerm.length > 0}
                            sortOptions={sortOptions}
                            onSetSort={this.handleSort}
                        />
                    </StyledSearchBarInnerContainer>
                </StyledSearchBarContainer>
                <StyledUIPaneResultsContainer editable={editable}>
                    <FetchingClickShield visible={isLoading} backgroundColor="#eee" />
                    {items.length > 0 &&
                        <InnerWhiteFrameContainer>
                            <ImageThumbnailGrid
                                columns={3}
                            >
                                {items.map(({
                                    asset,
                                    previewProps,
                                }) => (<AssetPreview
                                    key={asset.id}
                                    className={`recent-${asset.type}`}
                                    asset={asset}
                                    previewProps={previewProps}
                                    addAssetCallback={addAssetCallback ?? (() => { })}
                                    handleConfirm={handleConfirm ?? (() => { })}
                                    showContextMenu
                                    muted={false}
                                    hidePlaybacks={() => { }}
                                    backgroundVideoOnly={false}
                                    menuItems={[
                                        ...(isLibOrOwner ? [
                                            {
                                                label: "Asset Settings...",
                                                onClick: () => this.handleEditSettings(asset, previewProps, existingTags)
                                            },
                                        ] : []),
                                        ...(editable ? [
                                            {
                                                label: "Replace With...",
                                                onClick: () => this.handleReplaceItem(asset)
                                            },
                                            asset.get("hidden")
                                                ? {
                                                    label: "Show",
                                                    onClick: () => this.handleShowItem(asset)
                                                }
                                                : {
                                                    label: "Hide",
                                                    onClick: () => this.handleHideItem(asset)
                                                },
                                            {
                                                label: "Delete",
                                                onClick: () => this.handleDeleteItem(asset)
                                            }
                                        ] : [])
                                    ]}
                                />
                                ))}
                            </ImageThumbnailGrid>
                        </InnerWhiteFrameContainer>}
                    {items.length === 0 && (
                        (
                            !searchTerm &&
                            selectedMediaFilter === "all" &&
                            selectedTagFilter === "  all  "
                        )
                            ? <NoTeamAssetNotice />
                            : <NoMatchNotice />
                    )}
                </StyledUIPaneResultsContainer>
            </UIPane >
        );
    }
}
